<script setup lang="ts">
import { computed, onMounted, ref, watch, useSlots, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';

import AppTag from '../appTag/AppTag.vue';
import AppAlternativeFilterButton from '../appAlternativeFilterButton/AppAlternativeFilterButton.vue';

import type {
  AppPreferencesTagsSettings,
  TagData,
  TagDataWithSize,
} from './AppPreferencesTags.model';
import {
  calaculateAppPreferencesSettings,
  getElementSize,
} from './AppPreferencesTags.helpers';

interface AppPreferencesTagsProps {
  tags: TagData[];
  addPreferencesText: string;
  removeIconAriaLabel: string;
  isMobile: boolean;
  showFiltersAriaLabel: string;
  shouldHideAddButton?: boolean;
  showOnlySelectedFilters?: boolean;
  totalTags?: number;
}

const props = defineProps<AppPreferencesTagsProps>();

interface AppPreferencesTagsEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'remove-filter', value: { type: string; id?: string }): void;
  (eventName: 'show-more-filters'): void;
}

const emit = defineEmits<AppPreferencesTagsEmits>();

const appPreferencesTagsRef = useTemplateRef('appPreferencesTagsRef');
const slotContentRef = useTemplateRef('slotContentRef');
const tagsWithSizes = ref<TagDataWithSize[]>([]);
const { width: containerWidth } = useElementSize(appPreferencesTagsRef);
const { width: slotWidth } = useElementSize(slotContentRef);

const slots = useSlots();
const hasSlotContent = computed(() => !!slots['after-add-button']);

watch(props, () => {
  updateTagsWithSizes();
});

function updateTagsWithSizes(): void {
  tagsWithSizes.value = props.tags.map((tag: TagData) => ({
    ...tag,
    width: getElementSize(tag),
  }));
}

const activeFiltersAmount = computed(
  () => props.totalTags ?? props.tags.length ?? tagsWithSizes.value.length
);

const preferencesSettings = computed<AppPreferencesTagsSettings>(() =>
  calaculateAppPreferencesSettings(
    containerWidth.value - (slotWidth.value || 0),
    tagsWithSizes.value
  )
);

const visiblePreferences = computed<TagDataWithSize[]>(() =>
  tagsWithSizes.value.slice(0, preferencesSettings.value.shownTagsAmount)
);

const shouldShowSelectedFilters = computed<boolean>(() => !props.isMobile);

function onAddFilter(): void {
  emit('add-filter');
}

onMounted(() => {
  updateTagsWithSizes();
});
</script>

<template>
  <div
    ref="appPreferencesTagsRef"
    class="app-preferences-tags"
    :class="{
      'mobile-layout': props.isMobile,
      'has-slot-content': hasSlotContent,
    }"
  >
    <!-- NOTE: First AppTag must be duplicated, because useSlot inside AppTag is not reactive, -->
    <!-- which causes this button to have additional white space between icon and non existing slot. -->
    <template v-if="!shouldHideAddButton">
      <AppTag
        v-if="props.tags.length === 0"
        :title="props.addPreferencesText"
        type="enabled-filter"
        :clickable="true"
        size="s"
        :icon="{
          name: 'plus',
          size: '18',
          position: 'left',
          color: 'color-white',
          ariaLabel: props.showFiltersAriaLabel,
        }"
        @click="onAddFilter"
      >
        <template #title>
          {{ props.addPreferencesText }}
        </template>
      </AppTag>
      <AppTag
        v-else
        title="add preferences"
        type="enabled-filter"
        :clickable="true"
        size="s"
        :icon="{
          name: 'plus',
          size: '18',
          position: 'left',
          color: 'color-white',
          ariaLabel: props.showFiltersAriaLabel,
        }"
        @click="onAddFilter"
      />
    </template>
    <template v-else-if="!props.showOnlySelectedFilters">
      <AppAlternativeFilterButton
        compact
        type="modern"
        :active-filters-amount="activeFiltersAmount"
        text=""
        :show-filters-aria-label="props.showFiltersAriaLabel"
        @click="onAddFilter"
      />
    </template>
    <div
      v-if="hasSlotContent"
      ref="slotContentRef"
      class="slot-content-wrapper"
    >
      <slot name="after-add-button" />
    </div>
    <template v-if="shouldShowSelectedFilters">
      <AppTag
        v-for="preference in visiblePreferences"
        :key="preference.type"
        :title="preference.text"
        type="filter"
        :clickable="false"
        size="s"
        :text-wrap="false"
        :icon="{
          name: 'closeInCircle',
          size: '18',
          position: 'right',
          color: 'color-neutral-70',
          hoverColor: 'color-neutral-70',
          clickable: true,
          ariaLabel: props.removeIconAriaLabel,
        }"
        @icon-click="
          emit('remove-filter', { type: preference.type, id: preference.id })
        "
      >
        <template #title>
          {{ preference.text }}
        </template>
      </AppTag>
    </template>

    <AppTag
      v-if="
        preferencesSettings.hiddenTagsAmount > 0 && shouldShowSelectedFilters
      "
      title="+"
      type="filter"
      :clickable="true"
      :text-wrap="false"
      size="s"
      @click="emit('show-more-filters')"
    >
      <template #title>+ {{ preferencesSettings.hiddenTagsAmount }}</template>
    </AppTag>
    <div
      v-if="props.isMobile && visiblePreferences.length > 0"
      class="preferences-wrapper"
    >
      <AppTag
        v-for="preference in visiblePreferences"
        :key="preference.type"
        :title="preference.text"
        type="filter"
        :clickable="false"
        size="s"
        :text-wrap="false"
        :icon="{
          name: 'closeInCircle',
          size: '18',
          position: 'right',
          color: 'color-neutral-70',
          hoverColor: 'color-neutral-70',
          clickable: true,
          ariaLabel: props.removeIconAriaLabel,
        }"
        @icon-click="
          emit('remove-filter', { type: preference.type, id: preference.id })
        "
      >
        <template #title>
          {{ preference.text }}
        </template>
      </AppTag>

      <AppTag
        v-if="preferencesSettings.hiddenTagsAmount > 0"
        title="+"
        type="filter"
        :clickable="true"
        :text-wrap="false"
        size="s"
        :aria-label="props.showFiltersAriaLabel"
        @click="emit('show-more-filters')"
      >
        <template #title>+ {{ preferencesSettings.hiddenTagsAmount }}</template>
      </AppTag>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;

.app-preferences-tags {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-12);
  overflow: hidden;

  &.has-slot-content {
    overflow: visible;
  }

  &.mobile-layout {
    display: flex;
    flex-direction: column;
    align-items: end;
    gap: var(--spacing-16);
    .preferences-wrapper {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-8);
    }
  }
}
</style>
